import { defineComponent as _defineComponent } from 'vue';
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, normalizeStyle as _normalizeStyle, mergeProps as _mergeProps, normalizeClass as _normalizeClass, withCtx as _withCtx, createBlock as _createBlock, withKeys as _withKeys, vModelText as _vModelText, withDirectives as _withDirectives, Transition as _Transition } from "vue";
const _hoisted_1 = { class: "group-mgmt" };
const _hoisted_2 = { class: "group-mgmt__hint" };
const _hoisted_3 = { class: "group-mgmt__scroll" };
const _hoisted_4 = {
    key: 0,
    class: "group-mgmt__empty"
};
const _hoisted_5 = {
    key: 1,
    class: "group-mgmt__empty"
};
const _hoisted_6 = ["onMouseenter"];
const _hoisted_7 = { class: "group-mgmt__palette" };
const _hoisted_8 = ["onClick"];
const _hoisted_9 = {
    key: 1,
    class: "group-mgmt__dot"
};
const _hoisted_10 = ["value", "aria-label", "onBlur"];
const _hoisted_11 = {
    key: 3,
    class: "group-mgmt__name"
};
const _hoisted_12 = ["onClick"];
const _hoisted_13 = {
    key: 0,
    class: "group-mgmt__members"
};
const _hoisted_14 = {
    key: 1,
    class: "group-mgmt__member-list"
};
const _hoisted_15 = {
    key: 2,
    class: "group-mgmt__member-list group-mgmt__member-list--empty"
};
const _hoisted_16 = {
    key: 2,
    class: "group-mgmt__row group-mgmt__row--adding"
};
const _hoisted_17 = { class: "group-mgmt__dot" };
const _hoisted_18 = { class: "group-mgmt__status" };
const _hoisted_19 = {
    key: 0,
    class: "group-mgmt__msg group-mgmt__msg--error"
};
const _hoisted_20 = {
    key: 1,
    class: "group-mgmt__msg group-mgmt__msg--success"
};
import { computed, nextTick, onMounted, ref } from "vue";
import { TAG_COLORS } from "@/constants/tagColors";
import { useSettingsSaveFlow } from "@/composables/useSettingsSaveFlow";
import { groupsService } from "@/services/groupsService";
import { useAuthStore, useGroupsStore, useInboxStore, useConfirmDialogStore } from "@/stores";
export default /*@__PURE__*/ _defineComponent({
    __name: 'GroupManagement',
    setup(__props) {
        const store = useGroupsStore();
        const authStore = useAuthStore();
        const inboxStore = useInboxStore();
        const confirmDialog = useConfirmDialogStore();
        const { error, successMsg, run } = useSettingsSaveFlow();
        const palette = TAG_COLORS;
        const colorNames = Object.keys(TAG_COLORS);
        const adding = ref(false);
        const newName = ref("");
        const newColor = ref(colorNames[0]);
        const newNameInput = ref(null);
        const hoverId = ref(null);
        const expandedId = ref(null);
        const savingId = ref(null);
        const canEdit = computed(() => Boolean(authStore.userInfo?.isAdmin));
        const candidates = computed(() => {
            const info = authStore.userInfo || {};
            const me = info.pk == null ? [] : [{ id: info.pk, label: `${fullName(info)} (me)`.trim(), email: info.email }];
            const teammates = (inboxStore.teammates || []).map((teammate) => ({
                id: teammate.id,
                label: fullName(teammate),
                email: teammate.email,
            }));
            return [...me, ...teammates];
        });
        function fullName(person) {
            return `${person.firstName || ""} ${person.lastName || ""}`.trim() || person.email || "Unknown user";
        }
        function hexOf(color) {
            return palette[color] || palette.grey;
        }
        function toggleMembers(id) {
            expandedId.value = expandedId.value === id ? null : id;
            if (expandedId.value !== null && canEdit.value)
                inboxStore.fetchTeammates().catch(() => { });
        }
        /** The first colour no group holds, falling back to a cycle once all of them are taken. */
        function freshColor() {
            const taken = new Set(store.groups.map((group) => group.color));
            return colorNames.find((name) => !taken.has(name)) ?? colorNames[store.groups.length % colorNames.length];
        }
        function startAdd() {
            adding.value = true;
            newName.value = "";
            newColor.value = freshColor();
            nextTick(() => newNameInput.value?.focus());
        }
        function cancelAdd() {
            adding.value = false;
            newName.value = "";
        }
        async function confirmAdd() {
            if (!adding.value)
                return;
            const name = newName.value.trim();
            adding.value = false;
            newName.value = "";
            if (!name)
                return;
            await run(async () => {
                const group = await groupsService.create(name, newColor.value);
                store.upsert(group);
                expandedId.value = group.id;
                inboxStore.fetchTeammates().catch(() => { });
            }, { successText: `"${name}" created.`, errorText: "Failed to create the group." });
        }
        async function rename(group, input) {
            const name = input.value.trim();
            if (name && name !== group.name) {
                await run(async () => store.upsert(await groupsService.update(group.id, { name })), {
                    successText: "Group renamed.",
                    errorText: "Failed to rename the group.",
                });
            }
            input.value = store.byId.get(group.id)?.name ?? group.name;
        }
        async function setColor(group, color) {
            if (color === group.color)
                return;
            await run(async () => store.upsert(await groupsService.update(group.id, { color })), {
                successText: "Colour updated.",
                errorText: "Failed to update the colour.",
            });
        }
        async function setMembers(group, userIds) {
            savingId.value = group.id;
            await run(async () => store.upsert(await groupsService.setMembers(group.id, userIds)), {
                successText: "Members updated.",
                errorText: "Failed to update the members.",
            });
            savingId.value = null;
        }
        async function remove(group) {
            const confirmed = await confirmDialog.confirm({
                title: `Delete ${group.name}?`,
                message: "The group stops appearing in pickers and comes off the cases it holds. " +
                    "Past notifications still show it, and nothing is deleted from those cases' history.",
                confirmText: "Delete group",
                confirmColor: "error",
            });
            if (!confirmed)
                return;
            await run(async () => {
                await groupsService.remove(group.id);
                store.drop(group.id);
            }, { successText: `"${group.name}" deleted.`, errorText: "Failed to delete the group." });
        }
        onMounted(() => {
            store.fetchGroups().catch(() => {
                error.value = "Failed to load groups.";
            });
        });
        return (_ctx, _cache) => {
            const _component_v_progress_circular = _resolveComponent("v-progress-circular");
            const _component_v_menu = _resolveComponent("v-menu");
            const _component_v_icon = _resolveComponent("v-icon");
            const _component_v_btn = _resolveComponent("v-btn");
            const _component_v_autocomplete = _resolveComponent("v-autocomplete");
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, _toDisplayString(canEdit.value
                    ? "A group can be assigned and tagged like a teammate. Everyone in it is notified."
                    : "Groups are managed by your administrator. Assign or tag one like a teammate."), 1),
                _createElementVNode("div", _hoisted_3, [
                    (_unref(store).loading && !_unref(store).loaded)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                            _createVNode(_component_v_progress_circular, {
                                indeterminate: "",
                                size: "18",
                                width: "2"
                            }),
                            _cache[3] || (_cache[3] = _createTextVNode(" Loading groups… ", -1))
                        ]))
                        : (!_unref(store).groups.length && !adding.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_5, _toDisplayString(canEdit.value ? 'No groups yet. Click "+ Add Group" below.' : "No groups yet."), 1))
                            : _createCommentVNode("", true),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(store).groups, (group) => {
                        return (_openBlock(), _createElementBlock("div", {
                            key: group.id,
                            class: "group-mgmt__group"
                        }, [
                            _createElementVNode("div", {
                                class: "group-mgmt__row",
                                onMouseenter: ($event) => (hoverId.value = group.id),
                                onMouseleave: _cache[1] || (_cache[1] = ($event) => (hoverId.value = null))
                            }, [
                                (canEdit.value)
                                    ? (_openBlock(), _createBlock(_component_v_menu, {
                                        key: 0,
                                        "close-on-content-click": true
                                    }, {
                                        activator: _withCtx(({ props: menuProps }) => [
                                            _createElementVNode("button", _mergeProps({ ref_for: true }, menuProps, {
                                                type: "button",
                                                class: "group-mgmt__dot group-mgmt__dot--clickable"
                                            }), [
                                                _createElementVNode("span", {
                                                    class: "group-mgmt__dot-inner",
                                                    style: _normalizeStyle({ background: hexOf(group.color) })
                                                }, null, 4)
                                            ], 16)
                                        ]),
                                        default: _withCtx(() => [
                                            _createElementVNode("div", _hoisted_7, [
                                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(TAG_COLORS), (hex, name) => {
                                                    return (_openBlock(), _createElementBlock("button", {
                                                        key: name,
                                                        type: "button",
                                                        class: _normalizeClass(["group-mgmt__swatch", { 'group-mgmt__swatch--active': group.color === name }]),
                                                        style: _normalizeStyle({ background: hex }),
                                                        onClick: ($event) => (setColor(group, name))
                                                    }, null, 14, _hoisted_8));
                                                }), 128))
                                            ])
                                        ]),
                                        _: 2
                                    }, 1024))
                                    : (_openBlock(), _createElementBlock("span", _hoisted_9, [
                                        _createElementVNode("span", {
                                            class: "group-mgmt__dot-inner",
                                            style: _normalizeStyle({ background: hexOf(group.color) })
                                        }, null, 4)
                                    ])),
                                (canEdit.value)
                                    ? (_openBlock(), _createElementBlock("input", {
                                        key: 2,
                                        value: group.name,
                                        class: "group-mgmt__input",
                                        placeholder: "Group name",
                                        "aria-label": `Rename ${group.name}`,
                                        onKeyup: _cache[0] || (_cache[0] = _withKeys(($event) => ($event.target.blur()), ["enter"])),
                                        onBlur: ($event) => (rename(group, $event.target))
                                    }, null, 40, _hoisted_10))
                                    : (_openBlock(), _createElementBlock("span", _hoisted_11, _toDisplayString(group.name), 1)),
                                _createElementVNode("button", {
                                    type: "button",
                                    class: "group-mgmt__count",
                                    onClick: ($event) => (toggleMembers(group.id))
                                }, [
                                    _createTextVNode(_toDisplayString(group.memberCount) + " " + _toDisplayString(group.memberCount === 1 ? "person" : "people") + " ", 1),
                                    _createVNode(_component_v_icon, { size: "14" }, {
                                        default: _withCtx(() => [
                                            _createTextVNode(_toDisplayString(expandedId.value === group.id ? "mdi-chevron-up" : "mdi-chevron-down"), 1)
                                        ]),
                                        _: 2
                                    }, 1024)
                                ], 8, _hoisted_12),
                                (canEdit.value)
                                    ? (_openBlock(), _createBlock(_component_v_btn, {
                                        key: 4,
                                        icon: "mdi-close",
                                        variant: "text",
                                        size: "x-small",
                                        class: _normalizeClass(["group-mgmt__delete", { 'group-mgmt__delete--visible': hoverId.value === group.id }]),
                                        "aria-label": `Delete ${group.name}`,
                                        onClick: ($event) => (remove(group))
                                    }, null, 8, ["class", "aria-label", "onClick"]))
                                    : _createCommentVNode("", true)
                            ], 40, _hoisted_6),
                            (expandedId.value === group.id)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                                    (canEdit.value)
                                        ? (_openBlock(), _createBlock(_component_v_autocomplete, {
                                            key: 0,
                                            "model-value": group.members.map((member) => member.id),
                                            items: candidates.value,
                                            "item-title": "label",
                                            "item-value": "id",
                                            label: "People in this group",
                                            multiple: "",
                                            chips: "",
                                            "closable-chips": "",
                                            "hide-details": "",
                                            density: "compact",
                                            loading: savingId.value === group.id,
                                            "onUpdate:modelValue": ($event) => (setMembers(group, $event))
                                        }, null, 8, ["model-value", "items", "loading", "onUpdate:modelValue"]))
                                        : (group.members.length)
                                            ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(group.members, (member) => {
                                                    return (_openBlock(), _createElementBlock("span", {
                                                        key: member.id,
                                                        class: "group-mgmt__member"
                                                    }, _toDisplayString(fullName(member)), 1));
                                                }), 128))
                                            ]))
                                            : (_openBlock(), _createElementBlock("div", _hoisted_15, "Nobody in this group yet."))
                                ]))
                                : _createCommentVNode("", true)
                        ]));
                    }), 128)),
                    (adding.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                            _createElementVNode("span", _hoisted_17, [
                                _createElementVNode("span", {
                                    class: "group-mgmt__dot-inner",
                                    style: _normalizeStyle({ background: hexOf(newColor.value) })
                                }, null, 4)
                            ]),
                            _withDirectives(_createElementVNode("input", {
                                ref_key: "newNameInput",
                                ref: newNameInput,
                                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((newName).value = $event)),
                                class: "group-mgmt__input",
                                placeholder: "New group name",
                                "aria-label": "New group name",
                                onKeyup: [
                                    _withKeys(confirmAdd, ["enter"]),
                                    _withKeys(cancelAdd, ["escape"])
                                ],
                                onBlur: confirmAdd
                            }, null, 544), [
                                [_vModelText, newName.value]
                            ])
                        ]))
                        : _createCommentVNode("", true),
                    (canEdit.value)
                        ? (_openBlock(), _createElementBlock("button", {
                            key: 3,
                            type: "button",
                            class: "group-mgmt__add-btn",
                            onClick: startAdd
                        }, "+ Add Group"))
                        : _createCommentVNode("", true)
                ]),
                _createElementVNode("div", _hoisted_18, [
                    _createVNode(_Transition, { name: "group-mgmt-fade" }, {
                        default: _withCtx(() => [
                            (_unref(error))
                                ? (_openBlock(), _createElementBlock("span", _hoisted_19, _toDisplayString(_unref(error)), 1))
                                : (_unref(successMsg))
                                    ? (_openBlock(), _createElementBlock("span", _hoisted_20, _toDisplayString(_unref(successMsg)), 1))
                                    : _createCommentVNode("", true)
                        ]),
                        _: 1
                    })
                ])
            ]));
        };
    }
});
